// Air HUD (UI-4): 10 bubbles that drain while the player's head is underwater,
// so drowning damage (combat.ts tickAir) stops arriving unannounced. Bubbles pop
// as the last one empties, then the row hides once you surface and refill. Shown
// only while submerged, and never in creative/spectator. Sprite painted once
// (pixel-art, no assets), matching the hunger/hearts rows. Client (ENG-1).
import { h } from '../h';
import { SURVIVAL } from '../../../sim/config';
import { modeCaps } from '../../../sim/game/modes';
import type { Game } from '../../boot';

const SIZE = 9;
const BUBBLES = 10;

/** kind 0 = gone, 1 = popping, 2 = full. */
function bubbleURL(kind: number): string {
  const cvs = document.createElement('canvas');
  cvs.width = SIZE; cvs.height = SIZE;
  const ctx = cvs.getContext('2d')!;
  if (kind === 0) return cvs.toDataURL(); // empty slot draws nothing
  const mask = kind === 2
    ? ['.#####.', '#######', '#######', '#######', '#######', '#######', '.#####.']
    : ['.#...#.', '#.....#', '.......', '.......', '.......', '#.....#', '.#...#.'];
  for (let y = 0; y < mask.length; y++) {
    for (let x = 0; x < mask[y]!.length; x++) {
      if (mask[y]![x] !== '#') continue;
      // A light rim on the upper-left reads as a bubble rather than a dot.
      const rim = kind === 2 && (y <= 1 || x <= 1);
      ctx.fillStyle = rim ? '#dff2ff' : '#2f6fd0';
      ctx.fillRect(x + 1, y + 1, 1, 1);
    }
  }
  return cvs.toDataURL();
}

export class Air {
  readonly el: HTMLElement;
  private readonly cells: HTMLElement[] = [];
  private readonly kind: number[] = new Array(BUBBLES).fill(-1);
  private readonly urls = [bubbleURL(0), bubbleURL(1), bubbleURL(2)];
  private lastShow: boolean | null = null;

  constructor() {
    this.el = h('div', { class: 'bb-air' });
    for (let i = 0; i < BUBBLES; i++) {
      const c = h('div', { class: 'bb-stat-icon' });
      this.cells.push(c);
      this.el.append(c);
    }
  }

  update(game: Game): void {
    const p = game.playerState;
    // Only worth the screen space while actually losing air.
    const show = modeCaps(p.gameMode).survivalHunger && p.air < SURVIVAL.MAX_AIR;
    if (show !== this.lastShow) { this.el.style.display = show ? 'flex' : 'none'; this.lastShow = show; }
    if (!show) return;
    const per = SURVIVAL.MAX_AIR / BUBBLES;
    for (let slot = 0; slot < BUBBLES; slot++) {
      const left = p.air - slot * per;
      const kind = left >= per ? 2 : left > 0 ? 1 : 0;
      if (kind === this.kind[slot]) continue;
      this.kind[slot] = kind;
      this.cells[slot]!.style.backgroundImage = `url(${this.urls[kind]})`;
    }
  }
}
